import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

import { CAMPANHAS_DESIGN as D } from '../../Campanhas.design';

export default function PassoPublico({
    form,
    set,
    configCrm,
    optionsTags,
    contactLists,
    estimativa,
    loadingEstimativa,
    isWhatsAppBusiness,
    isEdit,
    onCsvChange,
    onSelecionarPipeline,
}) {
    // CSV só existe no WhatsApp oficial (regra herdada) ou em SMS, que não depende
    // de conversa prévia com o número.
    const permiteCsv = isWhatsAppBusiness || form.tipoEnvio === 'sms';

    const OPCOES = [
        { valor: 'funil', label: 'Funil', descricao: 'Contatos em estágios específicos do CRM', icon: 'mdi:filter-variant' },
        { valor: 'tag', label: 'Tags', descricao: 'Conversas marcadas com determinadas tags', icon: 'mdi:tag-outline' },
        { valor: 'lista_contatos', label: 'Lista de contatos', descricao: 'Listas montadas na agenda de contatos', icon: 'mdi:format-list-bulleted' },
        ...(permiteCsv
            ? [{ valor: 'csv', label: 'Arquivo CSV', descricao: 'Importe uma planilha com número e nome', icon: 'mdi:file-delimited-outline' }]
            : []),
    ];

    const pipelines = configCrm?.pipelines || [];
    // Os estagios NAO vem aninhados no pipeline: escolher um pipeline refaz a busca
    // do configCrm com ?pipeline_id=..., e o resultado chega em configCrm.stages.
    const estagios = configCrm?.stages || [];
    const mostraEstimativa = !['csv', 'lista_contatos'].includes(form.tipo);

    return (
        <div className="space-y-4">
            <div className={D.wizard.secao}>
                <p className={D.wizard.secaoTitulo}>Quem vai receber?</p>
                <p className={D.wizard.secaoSub}>Escolha de onde vem a lista de destinatários.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    {OPCOES.map((o) => (
                        <button
                            key={o.valor}
                            type="button"
                            onClick={() => set('tipo', o.valor)}
                            className={form.tipo === o.valor ? D.wizard.cardOpcaoAtiva : D.wizard.cardOpcao}
                        >
                            <div className={`${D.wizard.cardIcone} bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]`}>
                                <Iconify icon={o.icon} width={18} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">{o.label}</p>
                                <p className="text-[11px] text-gray-500 dark:text-[#ABABAD] leading-snug mt-0.5">
                                    {o.descricao}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {form.tipo === 'funil' && (
                <div className={`${D.wizard.secao} space-y-4`}>
                    <div>
                        <Label className={D.wizard.label}>Pipeline</Label>
                        <Select
                            value={form.funilId}
                            disabled={isEdit}
                            onValueChange={(v) => {
                                set('funilId', v);
                                set('funilIds', []);
                                onSelecionarPipeline(v);
                            }}
                        >
                            <SelectTrigger className={D.wizard.input}>
                                <SelectValue placeholder="Selecione o pipeline" />
                            </SelectTrigger>
                            <SelectContent>
                                {pipelines.map((p) => (
                                    <SelectItem key={p._id} value={p._id}>{p.nome}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {form.funilId && (
                        <div>
                            <Label className={D.wizard.label}>Estágios</Label>
                            {/* MultiSelect e nao-controlado: `defaultValue` semeia o estado
                                interno e `onValueChange` devolve so os ids. Guardamos os
                                objetos completos porque o backend le `funil_ids` direto. */}
                            <MultiSelect
                                key={form.funilId}
                                options={estagios.map((e) => ({ label: e.nome, value: e._id }))}
                                defaultValue={form.funilIds.map((f) => f._id || f)}
                                onValueChange={(ids) =>
                                    set('funilIds', estagios.filter((e) => ids.includes(e._id)))
                                }
                                placeholder="Selecione os estágios"
                                disabled={isEdit}
                            />
                        </div>
                    )}
                </div>
            )}

            {form.tipo === 'tag' && (
                <div className={D.wizard.secao}>
                    <Label className={D.wizard.label}>Tags</Label>
                    <MultiSelect
                        options={(optionsTags || []).map((t) => ({ label: t.nome, value: t._id || t.id }))}
                        defaultValue={form.tagsIds.map((t) => t._id || t.id || t)}
                        onValueChange={(ids) =>
                            set('tagsIds', (optionsTags || []).filter((t) => ids.includes(t._id || t.id)))
                        }
                        placeholder="Selecione as tags"
                        disabled={isEdit}
                    />
                </div>
            )}

            {form.tipo === 'lista_contatos' && (
                <div className={D.wizard.secao}>
                    <Label className={D.wizard.label}>Listas de contatos</Label>
                    <MultiSelect
                        options={(contactLists || []).map((l) => ({ label: l.nome, value: l._id }))}
                        defaultValue={form.selectedContactLists.map((l) => l._id || l)}
                        onValueChange={(ids) =>
                            set('selectedContactLists', (contactLists || []).filter((l) => ids.includes(l._id)))
                        }
                        placeholder="Selecione as listas"
                    />
                </div>
            )}

            {form.tipo === 'csv' && (
                <div className={D.wizard.secao}>
                    <Label className={D.wizard.label}>Arquivo CSV</Label>
                    <Input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => onCsvChange(e.target.files?.[0] || null)}
                        className={D.wizard.input}
                    />
                    <div className={`${D.wizard.avisoInfo} mt-3`}>
                        <Iconify icon="mdi:information-outline" width={14} className="flex-shrink-0 mt-px" />
                        <span>
                            O arquivo precisa ter uma coluna <strong>numero</strong> (ou telefone/phone/celular).
                            A coluna <strong>nome</strong> é opcional e usada na variável {'{{nome}}'}.
                        </span>
                    </div>
                    {form.csvFile && (
                        <p className="text-[11px] text-gray-500 dark:text-[#ABABAD] mt-2 truncate">
                            {form.csvFile.name}
                        </p>
                    )}
                </div>
            )}

            {['funil', 'tag'].includes(form.tipo) && (
                <div className={D.wizard.secao}>
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <p className={D.wizard.secaoTitulo}>Filtrar por período de atividade</p>
                            <p className={D.wizard.secaoSub}>Considera apenas contatos ativos no intervalo.</p>
                        </div>
                        <Switch checked={form.isFiltroData} onCheckedChange={(v) => set('isFiltroData', v)} />
                    </div>

                    {form.isFiltroData && (
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <div>
                                <Label className={D.wizard.label}>De</Label>
                                <Input
                                    type="date"
                                    value={form.dataInicio}
                                    onChange={(e) => set('dataInicio', e.target.value)}
                                    className={D.wizard.input}
                                />
                            </div>
                            <div>
                                <Label className={D.wizard.label}>Até</Label>
                                <Input
                                    type="date"
                                    value={form.dataFinal}
                                    onChange={(e) => set('dataFinal', e.target.value)}
                                    className={D.wizard.input}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {(mostraEstimativa || form.tipo === 'csv') && form.tipo && (
                <div className={D.wizard.secao}>
                    <div className="flex items-center gap-3">
                        <div className={`${D.wizard.cardIcone} bg-violet-50 dark:bg-violet-500/10 text-[#7C3AED]`}>
                            <Iconify icon="mdi:account-group-outline" width={18} />
                        </div>
                        <div>
                            {loadingEstimativa ? (
                                <p className="text-sm text-gray-500 dark:text-[#ABABAD] flex items-center gap-2">
                                    <Iconify icon="mdi:loading" width={14} className="animate-spin" />
                                    Calculando...
                                </p>
                            ) : (
                                <>
                                    <p className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3] tabular-nums">
                                        {estimativa === null || estimativa === undefined ? '—' : estimativa}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-[#ABABAD]">
                                        destinatários estimados
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

PassoPublico.propTypes = {
    form: PropTypes.object.isRequired,
    set: PropTypes.func.isRequired,
    configCrm: PropTypes.object,
    optionsTags: PropTypes.array,
    contactLists: PropTypes.array,
    estimativa: PropTypes.number,
    loadingEstimativa: PropTypes.bool,
    isWhatsAppBusiness: PropTypes.bool,
    isEdit: PropTypes.bool,
    onCsvChange: PropTypes.func.isRequired,
    onSelecionarPipeline: PropTypes.func.isRequired,
};
